import * as React from 'react';
import {useTruncation} from '../../hooks';
import {useMergeRefs} from '@floating-ui/react';
import {Tooltip} from '../Tooltip';
import {LinkBase} from './LinkBase';
import {emptyObject} from '@packages/utilities/fp/utils';
import type {LinkProps} from './_types';

export const TruncatedLink = React.forwardRef<HTMLElement, LinkProps>(
  (
    {
      children,
      truncateToLines,
      truncationLimit,
      truncationPosition = 'end',
      truncationTooltipProps = emptyObject,
      ...props
    },
    ref
  ) => {
    const {
      truncationStyles,
      content,
      didCustomTruncate,
      textRef,
      isCSSTruncated,
    } = useTruncation({
      truncateToLines,
      truncationLimit,
      truncationPosition,
      shouldNotShowTooltip: truncationTooltipProps?.shouldNotShowTooltip,
      children,
    });

    const mergedRef = useMergeRefs([textRef, ref]);

    const shouldShowTooltip =
      !truncationTooltipProps.shouldNotShowTooltip &&
      ((didCustomTruncate && typeof children === 'string') || isCSSTruncated);

    // if an inline element is specified in addition to truncation, we want to use the truncation variant that changes the display to inline-block
    const truncationVariant =
      truncateToLines && Number(truncateToLines) === 1
        ? 'truncated-inline'
        : 'truncated';

    return (
      <Tooltip
        asChild
        isDisabled={!shouldShowTooltip}
        label={children}
        {...truncationTooltipProps}
      >
        <LinkBase
          {...(truncateToLines && Number(truncateToLines) === 1
            ? {truncation: truncationVariant}
            : {})}
          {...props}
          ref={mergedRef}
          style={truncationStyles}
        >
          {content}
        </LinkBase>
      </Tooltip>
    );
  }
);
